<template>
  <!-- CHIP-AUDIENCE: visible only to Full / Franqueado / Gerente.
       Operador and Atendente have no credit-management context. -->
  <button
    v-if="canSeeChip"
    class="assistant-credit-chip"
    :class="{ 'assistant-credit-chip--insufficient': state === 'insufficient' }"
    data-testid="assistant-credit-chip"
    type="button"
    :aria-label="t('Selecionar franquia para créditos')"
    @click="handleClick"
  >
    <i class="pi pi-building assistant-credit-chip__icon" aria-hidden="true" />
    <span class="assistant-credit-chip__name">{{ franchiseName }}</span>
    <span
      class="assistant-credit-chip__balance"
      data-testid="assistant-credit-chip-balance"
    >{{ humanizedBalance }}</span>
    <i class="pi pi-chevron-down assistant-credit-chip__chevron" aria-hidden="true" />
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { permissionsService } from '../../services/permissionsService'
import { useCreditWallet } from '../../composables/useCreditWallet'
import { formatCredits } from './formatCredits'
import { eventBus } from '../../eventBus'

const { t } = useI18n()

/**
 * CHIP-AUDIENCE gate: only Full / Franqueado / Gerente see the credit chip.
 * Operador and Atendente have no credit-management context.
 */
const canSeeChip = computed<boolean>(() =>
  permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']),
)

const { selectedFranquiaId, balanceMicro, state, fetchWallet } = useCreditWallet()

/**
 * Humanized balance in credits (e.g. '200,1 cr'), derived from balance_micro.
 * The backend's balance_display is a RAW 6-decimal string ('200.100022') and
 * must never be rendered. Falls back to '—' while the wallet has not loaded.
 */
const humanizedBalance = computed<string>(() => {
  if (state.value === 'idle') return '—'
  return `${formatCredits(balanceMicro.value)} ${t('cr')}`
})

/** All franchises the user can access, derived from permissionsService. */
const franquias = computed(() => permissionsService.getFranquiasSelectArray())

/** Display name for the currently selected franchise. */
const franchiseName = computed<string>(() => {
  if (selectedFranquiaId.value === null) return t('Franquia')
  const found = franquias.value.find((f) => Number(f.ID) === selectedFranquiaId.value)
  return found ? found.Name : t('Franquia')
})

function handleClick(): void {
  eventBus.emit('modal.assistant-franchise.open')
}

// Auto-select and load wallet when the component mounts if no franchise is selected yet.
onMounted(() => {
  const list = franquias.value
  if (list.length > 0 && selectedFranquiaId.value === null) {
    selectedFranquiaId.value = Number(list[0].ID)
  }
  if (selectedFranquiaId.value !== null) {
    void fetchWallet(selectedFranquiaId.value)
  }
})

// If the franchise list changes (after permissions load) and we still have no selection,
// pick the first one and fetch.
watch(franquias, (list) => {
  if (list.length > 0 && selectedFranquiaId.value === null) {
    selectedFranquiaId.value = Number(list[0].ID)
    void fetchWallet(Number(list[0].ID))
  }
})
</script>

<style scoped>
.assistant-credit-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.875rem;
  background: var(--p-surface-100);
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--p-text-color);
  transition: background 0.15s ease;
  min-width: 0;
}

.assistant-credit-chip:hover {
  background: var(--p-surface-200);
}

.assistant-credit-chip--insufficient {
  background: var(--p-red-50, #fef2f2);
  color: var(--p-red-600, #dc2626);
}

.assistant-credit-chip--insufficient:hover {
  background: var(--p-red-100, #fee2e2);
}

.assistant-credit-chip__icon {
  font-size: 0.875rem;
  flex-shrink: 0;
}

.assistant-credit-chip__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.assistant-credit-chip__balance {
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

.assistant-credit-chip__chevron {
  font-size: 0.75rem;
  flex-shrink: 0;
  color: var(--p-text-muted-color, #94a3b8);
}
</style>
